import { useMutation, useQuery } from '@apollo/client/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  CreateGuestPatientFollowupCommentDocument,
  CreatePatientFollowupCommentDocument,
  GetCurrentPractitionerDocument,
  GetPatientDetailDialogDocument,
  GetPatientFollowupCommentsDocument,
} from '~/graphql/generated/graphql';
import { getMe } from '~/graphql/utility';
import {
  eventPageInfoState,
  patientDetailDialogState,
} from '~/state/partials/patient_detail_dialog/atoms';

import { FollowupCommentForm } from '.';
import { Fields } from './types';

export const useCreateComment = (
  patientType: 'Patient' | 'DirectVisitor' | 'WebVisitor' | 'LineVisitor' | undefined,
) => {
  const formRef = useRef<RefAttributeType<typeof FollowupCommentForm> | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const patientDetailState = useAtomValue(patientDetailDialogState);
  const { data } = useQuery(GetCurrentPractitionerDocument);
  const currentPractitioner = data ? getMe(data) : undefined;

  const patientId = patientType === 'Patient' ? patientDetailState.patientId : null;
  const guestPatientId =
    patientType === 'DirectVisitor' || patientType === 'WebVisitor' || patientType === 'LineVisitor'
      ? patientDetailState.patientId
      : null;

  const setEventInfoState = useSetAtom(eventPageInfoState);
  const [createPatientComment, { data: createdPatientCommentData }] = useMutation(
    CreatePatientFollowupCommentDocument,
    {
      refetchQueries: [
        {
          query: GetPatientDetailDialogDocument,
          variables: {
            id: patientId,
          },
        },
        {
          query: GetPatientFollowupCommentsDocument,
          variables: {
            id: patientId,
          },
        },
      ],
    },
  );
  const [createGuestPatientComment, { data: createdGuestPatientCommentData }] = useMutation(
    CreateGuestPatientFollowupCommentDocument,
    {
      refetchQueries: [
        {
          query: GetPatientDetailDialogDocument,
          variables: {
            id: guestPatientId,
          },
        },
        {
          query: GetPatientFollowupCommentsDocument,
          variables: {
            id: guestPatientId,
          },
        },
      ],
    },
  );

  useEffect(() => {
    if (
      !createdPatientCommentData?.createPatientFollowupComment &&
      !createdGuestPatientCommentData?.createGuestPatientFollowupComment
    ) {
      return;
    }
    setEventInfoState((_state) => ({ ..._state, isResetEvent: true, page: 1, perPage: 20 }));
  }, [createdGuestPatientCommentData, createdPatientCommentData, setEventInfoState]);

  const create = useCallback(
    async (values: Fields, status: null | 'in_progress' | 'finished') => {
      if (!currentPractitioner) {
        return;
      }
      setIsCreating(true);

      try {
        if (patientId) {
          await createPatientComment({
            variables: {
              input: {
                patientId,
                practitionerId: currentPractitioner.id,
                comment: values.comment,
                medicationFollowupStatus: status,
              },
            },
          });
        } else if (guestPatientId) {
          await createGuestPatientComment({
            variables: {
              input: {
                guestPatientId,
                practitionerId: currentPractitioner.id,
                comment: values.comment,
                medicationFollowupStatus: status,
              },
            },
          });
        }
        setError(null);
        setIsCreating(false);
      } catch (_error) {
        setError(_error?.message || 'エラーが発生しました');
        setIsCreating(false);
        throw new Error('', { cause: _error });
      }
    },
    [
      currentPractitioner,
      patientId,
      guestPatientId,
      createPatientComment,
      createGuestPatientComment,
    ],
  );

  return {
    create,
    formRef,
    error,
    isCreating,
  };
};
